import type { FeatureLike } from 'ol/Feature';
import MultiLineString from 'ol/geom/MultiLineString';
import { Circle as CircleStyle, Fill, Stroke, Style, Text } from 'ol/style';
import type { BasemapId, RemainderVisibility, VectorLayerModel } from '../core/types';
import type { CategoryStyle, ClassificationConfig, LabelConfig, StyleConfig } from '../presentation/config';
import { colorWithOpacity, defaultLabelConfig } from '../presentation/config';
import type { MixedThematicPiece } from '../domain/mixedThematic';
import { chainedLines } from './boundaryChains';

export interface FragmentStyleOptions {
  readonly selectedIds: ReadonlySet<string>;
  readonly scopedIds: ReadonlySet<string>;
  readonly remainderVisibility: RemainderVisibility;
  readonly styleConfig: StyleConfig;
  readonly classificationConfig: ClassificationConfig;
  readonly categoryByFragmentId: ReadonlyMap<string, string>;
  readonly hasValueByFragmentId?: ReadonlyMap<string, boolean>;
  readonly classificationSubsetIds?: ReadonlySet<string>;
  readonly basemap: BasemapId;
  readonly editingFragmentId?: string | null;
  /** V1.8.1 secondary outline when «Ver por» = Fragmentos (keyed by fragment id). */
  readonly secondary?: SecondaryOutlineOptions;
}

const categoriesByValue = (config: ClassificationConfig): ReadonlyMap<string, CategoryStyle> => new Map(config.categories.map((category) => [category.value, category]));
const paintsFill = (config: ClassificationConfig): boolean => config.paint === 'fill' || config.paint === 'fill-outline';
const paintsOutline = (config: ClassificationConfig): boolean => config.paint === 'outline' || config.paint === 'fill-outline';
const thematicActive = (config: ClassificationConfig): boolean => config.mode === 'single' || Boolean(config.field);
const effectiveRemainderStyle = (config: ClassificationConfig): 'base' | 'dim' | 'hide' =>
  config.thematicView === 'classified-only' ? 'hide' : config.thematicView === 'context' ? 'dim' : 'base';

export interface BoundaryStyleOptions {
  readonly scopedIds: ReadonlySet<string>;
  readonly selectedEntityIds: ReadonlySet<string>;
  readonly remainderVisibility: RemainderVisibility;
  readonly basemap: BasemapId;
  readonly styleConfig: StyleConfig;
  readonly classificationConfig: ClassificationConfig;
  readonly categoryByEntityId: ReadonlyMap<string, string>;
  readonly hasValueByEntityId?: ReadonlyMap<string, boolean>;
  readonly classificationSubsetIds?: ReadonlySet<string>;
  readonly editingEntityKind?: 'suerte' | 'tablon';
  readonly editingEntityId?: string;
}

export interface EntityPresentationStyleOptions extends BoundaryStyleOptions {
  /** Suertes split by Áreas parciales (mixed view): fills come from these derived pieces, never from the entity. */
  readonly piecesByEntityId?: ReadonlyMap<string, readonly MixedThematicPiece[]>;
  /** V1.8.1 secondary classification: an outline per category on the entity boundary (the principal one only fills). */
  readonly secondary?: SecondaryOutlineOptions;
}

/** Secondary classification (B) as drawn: category per entity (or fragment), its colours and the line, already scaled. */
export interface SecondaryOutlineOptions {
  readonly categoryById: ReadonlyMap<string, string>;
  readonly categories: ReadonlyMap<string, CategoryStyle>;
  readonly width: number;
  readonly lineDash?: readonly number[];
}

/** Light casing under a secondary outline: legible over satellite imagery and next to the fill colours. */
const SECONDARY_CASING = 'rgba(255,255,255,.88)';

/** The visible B category of one entity, if any («Sin valor» and hidden categories draw the normal boundary). */
const secondaryCategory = (secondary: SecondaryOutlineOptions | undefined, id: string): CategoryStyle | undefined => {
  const category = secondary?.categories.get(secondary.categoryById.get(id) ?? '');
  return category?.visible ? category : undefined;
};

/**
 * B outline on a boundary geometry: a light casing, then the category colour. Drawn above the fills and the normal
 * boundaries and below selection/editing, so A stays readable inside and B around it.
 */
const secondaryOutlineStyles = (boundary: unknown, category: CategoryStyle, secondary: SecondaryOutlineOptions): Style[] => {
 // The logical boundary is one segment per edge: joined into polylines it draws the same line far faster.
 const geometry = boundary instanceof MultiLineString ? chainedLines(boundary) : boundary;
 return [
  new Style({ ...(geometry ? { geometry: geometry as never } : {}), stroke: new Stroke({ color: SECONDARY_CASING, width: secondary.width + 2.2 * (secondary.width / 3) }), zIndex: 14 }),
  new Style({ ...(geometry ? { geometry: geometry as never } : {}), stroke: new Stroke({ color: category.color, width: secondary.width, lineDash: secondary.lineDash ? [...secondary.lineDash] : undefined, lineCap: secondary.lineDash ? 'butt' : 'round', lineJoin: 'round' }), zIndex: 15 }),
 ];
};

export const fragmentStyle = (options: FragmentStyleOptions) => {
 const categories = categoriesByValue(options.classificationConfig);
 return (feature: FeatureLike): Style | undefined => {
  const fragmentId = String(feature.get('fragmentId'));
  if (options.editingFragmentId === fragmentId) return new Style({
    fill: new Fill({ color: 'rgba(255,0,168,.38)' }),
    stroke: new Stroke({ color: '#ff00a8', width: 4, lineDash: [10, 5] }),
    zIndex: 40,
  });
  const selected = options.selectedIds.has(fragmentId);
  const inScope = options.scopedIds.has(fragmentId);
  if (!inScope && options.remainderVisibility === 'hide') return undefined;
  const category = categories.get(options.categoryByFragmentId.get(fragmentId) ?? '');
  const inClassificationSubset = options.classificationSubsetIds?.has(fragmentId) ?? true;
  if (options.classificationConfig.field && !options.classificationConfig.showNoValue && options.hasValueByFragmentId?.get(fragmentId) === false) return undefined;
  if (thematicActive(options.classificationConfig) && !inClassificationSubset) {
    if (effectiveRemainderStyle(options.classificationConfig) === 'hide') return undefined;
    if (effectiveRemainderStyle(options.classificationConfig) === 'dim') return new Style({ fill: new Fill({ color: colorWithOpacity(options.classificationConfig.remainderColor, options.styleConfig.dimOpacity) }), zIndex: 1 });
  }
  if (options.classificationConfig.field && category && !category.visible) return undefined;
  const config = options.styleConfig;
  if (!inScope) {
    return new Style({
      fill: new Fill({ color: colorWithOpacity(config.fillColor, config.dimOpacity) }),
      stroke: config.showPhysicalFragments ? new Stroke({ color: colorWithOpacity(config.outlineColor, Math.min(config.dimOpacity * 2, 0.3)), width: 0.35 }) : undefined,
      zIndex: 0,
    });
  }
  const thematicFill = Boolean(options.classificationConfig.field && category && paintsFill(options.classificationConfig));
  const thematicOutline = Boolean(options.classificationConfig.field && category && paintsOutline(options.classificationConfig));
  const baseFill = thematicActive(options.classificationConfig) && !inClassificationSubset ? options.classificationConfig.remainderColor : config.fillColor;
  const outline = !selected && inClassificationSubset ? secondaryCategory(options.secondary, fragmentId) : undefined;
  if (outline) {
    // Physical fragments: one style (fill + B outline); a fragment is its own entity at this «Ver por».
    return new Style({
      fill: new Fill({ color: colorWithOpacity(thematicFill ? category!.color : baseFill, thematicFill ? options.classificationConfig.opacity : config.fillOpacity) }),
      stroke: new Stroke({ color: outline.color, width: options.secondary!.width, lineDash: options.secondary!.lineDash ? [...options.secondary!.lineDash] : undefined }),
      zIndex: 3,
    });
  }
  return new Style({
    fill: new Fill({ color: colorWithOpacity(
      selected ? config.selectionFillColor : (thematicFill ? category!.color : baseFill),
      selected ? Math.max(options.classificationConfig.opacity, 0.68) : (thematicFill ? options.classificationConfig.opacity : config.fillOpacity),
    ) }),
    stroke: config.showPhysicalFragments ? new Stroke({
      color: colorWithOpacity(selected ? config.selectionOutlineColor : (thematicOutline ? category!.color : config.outlineColor), selected ? 0.8 : 0.38),
      width: selected ? 0.85 : 0.45,
    }) : undefined,
    zIndex: selected ? 8 : 2,
  });
 };
};

export const boundaryStyle = (options: BoundaryStyleOptions) => {
 const categories = categoriesByValue(options.classificationConfig);
 return (feature: FeatureLike): Style | undefined => {
  const fragmentIds = (feature.get('fragmentIds') as readonly string[] | undefined) ?? [];
  const inScope = fragmentIds.some((id) => options.scopedIds.has(id));
  if (!inScope && options.remainderVisibility === 'hide') return undefined;
  const level = String(feature.get('level'));
  const suerteId = String(feature.get('suerteId') ?? '');
  const entityId = String(feature.get('entityId') ?? '');
  const editing = options.editingEntityKind === level && options.editingEntityId === entityId;
  if (editing) {
    return new Style({
      stroke: new Stroke({ color: '#00e5ff', width: Math.max(3.5, options.styleConfig.selectionOutlineWidth), lineDash: [12, 6] }),
      zIndex: 35,
    });
  }
  const categoryKey = options.classificationConfig.level === level ? entityId : options.classificationConfig.level === 'suerte' ? suerteId : '';
  const category = categories.get(options.categoryByEntityId.get(categoryKey) ?? '');
  const inClassificationSubset = fragmentIds.some((id) => options.classificationSubsetIds?.has(id) ?? true);
  if (options.classificationConfig.field && !options.classificationConfig.showNoValue && options.hasValueByEntityId?.get(categoryKey) === false) return undefined;
  if (thematicActive(options.classificationConfig) && !inClassificationSubset && effectiveRemainderStyle(options.classificationConfig) === 'hide') return undefined;
  if (options.classificationConfig.field && category && !category.visible) return undefined;
  const selected = options.selectedEntityIds.has(String(feature.get('entityId')));
  const satellite = options.basemap === 'satellite' || options.basemap === 'hybrid';
  const config = options.styleConfig;
  if (thematicActive(options.classificationConfig) && !inClassificationSubset && effectiveRemainderStyle(options.classificationConfig) === 'dim') {
    return new Style({ stroke: new Stroke({ color: colorWithOpacity(options.classificationConfig.remainderColor, Math.min(config.dimOpacity * 3, 0.3)), width: 0.6 }), zIndex: 3 });
  }
  if (!inScope) {
    if (level === 'tablon') return undefined;
    return new Style({ stroke: new Stroke({ color: colorWithOpacity(config.outlineColor, Math.min(config.dimOpacity * 3, 0.3)), width: 0.6 }), zIndex: 3 });
  }
  if (selected) {
    return new Style({
      stroke: new Stroke({ color: config.selectionOutlineColor, width: config.selectionOutlineWidth }),
      zIndex: 20,
    });
  }
  const primaryBoundary = level === 'finca' || level === 'suerte';
  return new Style({
    stroke: primaryBoundary
      ? new Stroke({
        color: category && options.classificationConfig.field && paintsOutline(options.classificationConfig)
          ? category.color
          : thematicActive(options.classificationConfig) && !inClassificationSubset
            ? options.classificationConfig.remainderColor
            : (satellite ? colorWithOpacity(config.outlineColor, 1) : config.outlineColor),
        width: category && options.classificationConfig.field && paintsOutline(options.classificationConfig)
          ? Math.max(config.outlineWidth, level === 'finca' ? 2.4 : 2)
          : level === 'finca' ? Math.max(config.outlineWidth, 2.2) : config.outlineWidth,
      })
      : new Stroke({ color: colorWithOpacity(config.outlineColor, satellite ? 0.75 : 0.6), width: Math.max(0.6, config.outlineWidth * 0.52), lineDash: [3, 3] }),
    zIndex: level === 'finca' ? 13 : level === 'suerte' ? 12 : 10,
  });
 };
};

/** One style callback per logical entity; fill uses its component-preserving geometry and outline its derived exterior boundary. */
export const entityPresentationStyle = (options: EntityPresentationStyleOptions) => {
 const categories = categoriesByValue(options.classificationConfig);
 return (feature: FeatureLike): Style | Style[] | undefined => {
  const fragmentIds = (feature.get('fragmentIds') as readonly string[] | undefined) ?? [];
  const entityId = String(feature.get('entityId') ?? '');
  const level = String(feature.get('entityLevel') ?? feature.get('level') ?? '');
  const inScope = fragmentIds.some((id) => options.scopedIds.has(id));
  if (!inScope && options.remainderVisibility === 'hide') return undefined;
  const category = categories.get(options.categoryByEntityId.get(entityId) ?? '');
  const inClassificationSubset = fragmentIds.some((id) => options.classificationSubsetIds?.has(id) ?? true);
  const activeTheme = thematicActive(options.classificationConfig);
  const config = options.styleConfig;
  const selected = options.selectedEntityIds.has(entityId);
  const editing = options.editingEntityKind === level && options.editingEntityId === entityId;
  const pieces = inScope ? options.piecesByEntityId?.get(entityId) : undefined;
  if (pieces) {
    // Each piece follows its own category's visibility and "Sin valor"; selection/editing paint the whole Suerte.
    const visiblePieces = pieces.filter((piece) => (categories.get(piece.category)?.visible ?? true) && (options.classificationConfig.showNoValue || piece.hasValue));
    if (!visiblePieces.length) return undefined;
    if (!selected && !editing) {
      // B belongs to the whole Suerte: its outline follows the logical boundary, never the Áreas parciales.
      const outline = secondaryCategory(options.secondary, entityId);
      const styles = mixedPieceStyles(feature, visiblePieces, categories, options, level);
      return outline ? [...styles, ...secondaryOutlineStyles(feature.get('boundaryGeometry'), outline, options.secondary!)] : styles;
    }
  } else {
    if (options.classificationConfig.field && !options.classificationConfig.showNoValue && options.hasValueByEntityId?.get(entityId) === false) return undefined;
    if (activeTheme && !inClassificationSubset && effectiveRemainderStyle(options.classificationConfig) === 'hide') return undefined;
    if (options.classificationConfig.field && category && !category.visible) return undefined;
  }
  const dimmed = activeTheme && !inClassificationSubset && effectiveRemainderStyle(options.classificationConfig) === 'dim';
  const thematicFill = Boolean(options.classificationConfig.field && category && paintsFill(options.classificationConfig));
  const thematicOutline = Boolean(options.classificationConfig.field && category && paintsOutline(options.classificationConfig));
  const baseFill = dimmed ? options.classificationConfig.remainderColor : config.fillColor;
  const fillColor = selected ? config.selectionFillColor : thematicFill ? category!.color : baseFill;
  const fillOpacity = selected ? Math.max(options.classificationConfig.opacity, .68) : dimmed ? config.dimOpacity : thematicFill ? options.classificationConfig.opacity : config.fillOpacity;
  const satellite = options.basemap === 'satellite' || options.basemap === 'hybrid';
  const boundaryColor = editing ? '#00e5ff' : selected ? config.selectionOutlineColor : thematicOutline ? category!.color : dimmed ? options.classificationConfig.remainderColor : config.outlineColor;
  const boundaryWidth = editing ? Math.max(3.5, config.selectionOutlineWidth) : selected ? config.selectionOutlineWidth : thematicOutline ? Math.max(config.outlineWidth, level === 'finca' ? 2.4 : 2) : level === 'finca' ? Math.max(config.outlineWidth, 2.2) : level === 'suerte' ? config.outlineWidth : Math.max(.6, config.outlineWidth * .52);
  const boundary = feature.get('boundaryGeometry');
  const fill = new Style({ fill: new Fill({ color: colorWithOpacity(fillColor, fillOpacity) }), zIndex: selected ? 18 : editing ? 17 : 2 });
  // B outlines the classified entities only (never the context/remainder); selection and editing keep priority.
  // B replaces the base boundary (same line, drawn under its casing): one stroke less per entity on every redraw.
  const outline = !selected && !editing && !dimmed && inScope ? secondaryCategory(options.secondary, entityId) : undefined;
  if (outline) return [fill, ...secondaryOutlineStyles(boundary, outline, options.secondary!)];
  return [
    fill,
    new Style({
      geometry: boundary,
      stroke: new Stroke({ color: satellite && !selected && !editing && !thematicOutline ? colorWithOpacity(boundaryColor, 1) : boundaryColor, width: boundaryWidth, lineDash: editing ? [12, 6] : level === 'tablon' ? [3, 3] : undefined }),
      zIndex: editing ? 35 : selected ? 20 : level === 'finca' ? 13 : level === 'suerte' ? 12 : 10,
    }),
  ];
 };
};

/**
 * A Suerte split by Áreas parciales: one fill per derived piece (base remnant + exceptions, never overlapping) and
 * the Suerte's own exterior boundary on top. It stays one logical feature, so hit-testing and selection are unchanged.
 */
function mixedPieceStyles(feature: FeatureLike, pieces: readonly MixedThematicPiece[], categories: ReadonlyMap<string, CategoryStyle>, options: EntityPresentationStyleOptions, level: string): Style[] {
  const config = options.styleConfig; const thematic = options.classificationConfig;
  const satellite = options.basemap === 'satellite' || options.basemap === 'hybrid';
  const styles = pieces.map((piece) => {
    const category = categories.get(piece.category);
    const fill = category && paintsFill(thematic) ? colorWithOpacity(category.color, thematic.opacity) : colorWithOpacity(config.fillColor, config.fillOpacity);
    const outlined = category && paintsOutline(thematic) && (piece.source === 'subarea' || piece.source === 'scenario' || thematic.paint === 'outline');
    return new Style({
      geometry: piece.geometry,
      fill: new Fill({ color: fill }),
      stroke: outlined ? new Stroke({ color: category.color, width: Math.max(1, config.outlineWidth * .8) }) : undefined,
      zIndex: piece.source === 'base' ? 2 : 3,
    });
  });
  styles.push(new Style({
    geometry: feature.get('boundaryGeometry'),
    stroke: new Stroke({ color: satellite ? colorWithOpacity(config.outlineColor, 1) : config.outlineColor, width: level === 'finca' ? Math.max(config.outlineWidth, 2.2) : config.outlineWidth }),
    zIndex: 12,
  }));
  return styles;
}

const labelCache = new Map<string, Style>();
export const labelStyle = (basemap: BasemapId, renderScale = 1, labelConfig: LabelConfig = defaultLabelConfig) => (feature: FeatureLike): Style => {
  const level = String(feature.get('level'));
  const text = String(feature.get('text'));
  const satellite = basemap === 'satellite' || basemap === 'hybrid';
  const { style: labelTextStyle, placement } = labelConfig;
  const key = `${basemap}:${renderScale}:${level}:${text}:${JSON.stringify(labelTextStyle)}:${JSON.stringify(placement)}`;
  const cached = labelCache.get(key);
  if (cached) return cached;
  const isSuerte = level === 'suerte';
  const automaticContrast = labelTextStyle.contrastMode === 'automatic';
  const textColor = automaticContrast && satellite ? '#fffef7' : labelTextStyle.textColor;
  const haloColor = automaticContrast && satellite ? 'rgba(9,25,19,.95)' : labelTextStyle.haloColor;
  const backgroundColor = automaticContrast && satellite ? 'rgba(8,31,23,.58)' : colorWithOpacity(labelTextStyle.backgroundColor, labelTextStyle.backgroundOpacity);
  const distance = labelTextStyle.fontSize * 1.8;
  const automaticX = placement.position === 'left' ? -distance * 1.8 : placement.position === 'right' ? distance * 1.8 : 0;
  const automaticY = placement.position === 'top' ? -distance : placement.position === 'bottom' ? distance : 0;
  const padding = labelTextStyle.padding * renderScale;
  const renderedStyle = new Style({
    text: new Text({
      text,
      font: `${labelTextStyle.bold ? 700 : 400} ${labelTextStyle.fontSize * (isSuerte ? 1 : .86) * renderScale}px Inter, system-ui, sans-serif`,
      textAlign: 'center',
      offsetX: (automaticX + placement.offsetX) * renderScale,
      offsetY: (automaticY + placement.offsetY) * renderScale,
      fill: new Fill({ color: textColor }),
      stroke: labelTextStyle.halo ? new Stroke({ color: haloColor, width: labelTextStyle.haloWidth * renderScale }) : undefined,
      padding: [padding, padding, padding, padding],
      backgroundFill: labelTextStyle.background ? new Fill({ color: backgroundColor }) : undefined,
      overflow: false,
    }),
    zIndex: isSuerte ? 30 : 25,
  });
  labelCache.set(key, renderedStyle);
  return renderedStyle;
};

const subareaLabel = (feature: FeatureLike, renderScale: number): Text =>
  new Text({ text: String(feature.get('Nombre') ?? feature.get('name') ?? ''), font: `700 ${10 * renderScale}px Inter, system-ui, sans-serif`, fill: new Fill({ color: '#6b2b12' }), stroke: new Stroke({ color: 'rgba(255,255,255,.95)', width: 3 * renderScale }), overflow: false });

export const genericVectorStyle = (
  layer: VectorLayerModel,
  selectedFeatureIds: ReadonlySet<string> | string | null,
  thematic?: { config: ClassificationConfig; categoryByFeatureId: ReadonlyMap<string, string>; subsetIds: ReadonlySet<string> },
  showSubareaLabels = true,
  renderScale = 1,
  /** Subareas acting as thematic exceptions: the Maestro composition carries their color, here only an outline. */
  exceptionOutlineIds?: ReadonlySet<string>,
) => (feature: FeatureLike): Style | undefined => {
  const featureId = String(feature.get('genericFeatureId'));
  const selected = typeof selectedFeatureIds === 'string'
    ? feature.get('genericFeatureId') === selectedFeatureIds
    : selectedFeatureIds?.has(featureId) ?? false;
  if (!selected && exceptionOutlineIds?.has(featureId)) {
    // Transparent fill (not none): the subarea stays clickable exactly as before.
    return new Style({
      fill: new Fill({ color: 'rgba(0,0,0,0)' }),
      stroke: new Stroke({ color: 'rgba(31,45,58,.85)', width: 1.2 * renderScale, lineDash: [4 * renderScale, 3 * renderScale] }),
      text: showSubareaLabels ? subareaLabel(feature, renderScale) : undefined,
      zIndex: 1,
    });
  }
  const category = thematic ? categoriesByValue(thematic.config).get(thematic.categoryByFeatureId.get(featureId) ?? '') : undefined;
  if (thematic?.config.field && !thematic.config.showNoValue && thematic.categoryByFeatureId.get(featureId) === 'Sin valor') return undefined;
  if (thematic && thematicActive(thematic.config) && !thematic.subsetIds.has(featureId) && effectiveRemainderStyle(thematic.config) === 'hide') return undefined;
  if (category && !category.visible) return undefined;
  const isRemainder = Boolean(thematic && thematicActive(thematic.config) && !thematic.subsetIds.has(featureId));
  const dimmed = Boolean(isRemainder && thematic && effectiveRemainderStyle(thematic.config) === 'dim');
  const thematicFill = category && thematic && paintsFill(thematic.config) ? category.color : undefined;
  const thematicStroke = category && thematic && paintsOutline(thematic.config) ? category.color : undefined;
  const strokeColor = selected ? '#ffb000' : thematicStroke ?? layer.style.strokeColor;
  const fillColor = colorWithOpacity(selected ? '#ffd35a' : thematicFill ?? (isRemainder ? thematic!.config.remainderColor : layer.style.fillColor), selected ? 0.72 : dimmed ? 0.08 : thematicFill ? thematic!.config.opacity : 0.38);
  const strokeWidth = (selected ? Math.max(3.5, layer.style.strokeWidth + 1.5) : layer.style.strokeWidth) * renderScale;
  return new Style({
    image: new CircleStyle({
      radius: (selected ? layer.style.pointRadius + 2 : layer.style.pointRadius) * renderScale,
      fill: new Fill({ color: fillColor }),
      stroke: new Stroke({ color: strokeColor, width: strokeWidth }),
    }),
    fill: new Fill({ color: fillColor }),
    stroke: new Stroke({ color: strokeColor, width: strokeWidth }),
    text: layer.kind === 'subarea' && showSubareaLabels ? subareaLabel(feature, renderScale) : undefined,
    zIndex: selected ? 100 : 1,
  });
};
